import { formatDateNumber } from '@seen/core/dates'
import { invariant } from '@seen/core/invariant'
import { ConditionClinicalStatus } from '@seen/core/models/conditions/constants'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { Badge, Separator } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import { type JSX, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Activity } from '#src/components/activity/Activity.tsx'
import { CONDITION_STATUS_COLORS } from '#src/components/clinical/statusConfigs.ts'
import { ConditionsCombobox } from '#src/components/ConditionsCombobox.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { LabelColor } from '#src/components/LabelColor.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { RowBadge } from '#src/components/TaskRowBadge.tsx'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { ConditionActivity } from '#src/pages/participants/conditions/ConditionActivity.tsx'
import { conditionSecondaryName } from '#src/utils/conditionSecondaryName.ts'
import { toast } from '#src/utils/toast.ts'

export function ParticipantConditionPage(): JSX.Element {
  const { conditionId } = useParams()
  invariant(conditionId, 'Condition ID is required')
  return (
    <ParticipantConditionPageComponent
      key={conditionId}
      conditionId={conditionId}
    />
  )
}

function ParticipantConditionPageComponent({
  conditionId,
}: {
  conditionId: string
}): JSX.Element {
  const navigate = useNavigate()
  const isReadOnly = useIsReadOnly()
  const mutate = usePatchMutation<Diagnosis>().mutateAsync
  const { data: condition, isLoading } = useRead(Diagnosis, conditionId)
  // Lifted so both the title button and the ⋯-menu Edit item open the same
  // picker, anchored to the title.
  const [editOpen, setEditOpen] = useState(false)

  if (isLoading) {
    return <Loader />
  }

  invariant(condition, `Condition not found: ${conditionId}`)

  const secondaryName = conditionSecondaryName(condition)

  return (
    <PageLayout contentClassName="mx-auto w-full max-w-[calc(76ch+3rem)]">
      <ScrollAreaHeader className="-mx-4 mt-2 flex items-center justify-between gap-1">
        {isReadOnly ? (
          <div className="-m-2 flex flex-col gap-0.5 p-2">
            <h1 className="text-3xl font-semibold">{condition.name}</h1>
            {secondaryName && (
              <span className="text-sm text-foreground-tertiary">
                {secondaryName}
              </span>
            )}
          </div>
        ) : (
          // Editing a condition replaces it with a real coded condition (correct
          // ICD-10 + name) rather than free-text renaming the display string.
          <ConditionsCombobox
            patientRef={condition.participantSubject}
            replaceTarget={condition}
            open={editOpen}
            onOpenChange={setEditOpen}
          >
            <button
              type="button"
              className="group relative -m-2 flex max-w-(--container-prose) flex-col items-start gap-0.5 rounded-md p-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-hidden"
            >
              <span className="flex items-center gap-2">
                <span className="text-3xl font-semibold">{condition.name}</span>
                <IconPencil className="size-5 shrink-0 text-foreground-tertiary opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
              </span>
              {secondaryName && (
                <span className="text-sm text-foreground-tertiary">
                  {secondaryName}
                </span>
              )}
            </button>
          </ConditionsCombobox>
        )}
        <MoreDropdownMenu>
          {!isReadOnly && (
            <DropdownMenuItem
              onSelect={() => {
                // Defer so the menu finishes closing (and returns focus) before
                // the picker opens, otherwise the close steals focus back.
                setTimeout(() => setEditOpen(true), 0)
              }}
            >
              <IconPencil className="-mt-px size-4 text-foreground-secondary" />
              Edit…
            </DropdownMenuItem>
          )}
          <DropdownMenuDeleteItem
            deleteConfirmationProps={{
              title: `Remove "${condition.name || 'condition'}"?`,
              description:
                "This removes the condition from the participant's record. It can still be recovered by contacting our team.",
              deleteText: 'Remove',
              onConfirm: () => {
                mutate({
                  instance: condition,
                  mutateFn: (c) => {
                    c.markDeleted()
                  },
                })
                  .then(() => {
                    toast.success(`${condition.name || 'Condition'} removed`)
                    void navigate(-1)
                  })
                  .catch((error: DefaultError) => {
                    toast.error(
                      `Failed to remove ${condition.name || 'condition'}: ${error}`
                    )
                  })
              },
            }}
          />
          <DropdownMenuMedplumLinkItem to={condition} />
        </MoreDropdownMenu>
      </ScrollAreaHeader>
      <ScrollAreaContent className="gap-6 p-0">
        <div className="flex items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1 text-sm">
            {condition.icd10Codes.length > 0 && (
              <span className="flex items-center gap-1">
                {condition.icd10Codes.map((code) => (
                  <Badge key={code} variant="code">
                    {code}
                  </Badge>
                ))}
              </span>
            )}
          </div>
          <ResourceFieldForm<Diagnosis, ConditionClinicalStatus>
            instance={condition}
            valueFn={() => condition.clinicalStatus}
            patchFn={(instance, value) => {
              instance.clinicalStatus = value
            }}
            render={({ field: { value, onChange, ...fieldRest } }) => (
              <Combobox
                {...fieldRest}
                options={[
                  {
                    label: 'Active',
                    value: ConditionClinicalStatus.ACTIVE,
                    keywords: ['active'],
                  },
                  {
                    label: 'Controlled',
                    value: ConditionClinicalStatus.CONTROLLED,
                    keywords: ['controlled'],
                  },
                  {
                    label: 'Resolved',
                    value: ConditionClinicalStatus.RESOLVED,
                    keywords: ['resolved'],
                  },
                ]}
                onSelect={(value) => {
                  onChange(value ?? ConditionClinicalStatus.ACTIVE)
                }}
              >
                <RowBadge
                  icon={
                    <LabelColor
                      color={CONDITION_STATUS_COLORS[value] ?? '#bec2c8'}
                    />
                  }
                  className="cursor-pointer"
                >
                  {value}
                </RowBadge>
              </Combobox>
            )}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium">Condition Onset</span>
          <ResourceFieldForm
            instance={condition}
            valueFn={(c) => c.onsetDateString}
            patchFn={(c, value) => {
              c.onsetDate = value
            }}
            render={({ field }) => (
              <DatePicker
                {...field}
                allowUndefined
                placeholder="Onset date"
                dateFormatter={formatDateNumber}
                clearText="Clear"
              />
            )}
          />
        </div>
        <Separator />
        <Activity resource={condition} subject={condition.participantSubject}>
          <ConditionActivity condition={condition} />
        </Activity>
      </ScrollAreaContent>
    </PageLayout>
  )
}
